import { isAcPowerTerminalId, isAcTerminalId } from "../ac";
import { computeBatteryStacks } from "../battery-stack";
import type { DefLookup } from "../catalog";
import { effectiveTerminals } from "../effective";
import { JUNCTION_DEFINITION_ID } from "../node-kind";
import { getSimulateStateSpec } from "../simulate/states";
import type { ComponentDefinition, ComponentNode, Issue, System, Terminal } from "../types";
import { matchesAcSafetyIdentity } from "./ac-safety-identity";
import { buildGraph, intraLinks, v, type Edge, type Graph, type IntraLink, type Vertex } from "./graph";

/** Shore inlet PE represents the external supply earth, not a neutral bond. */
const SHORE_INLETS = new Set([
  "src-shore-16a", "src-shore-30a-us", "src-shore-50a-us", "src-smartplug-bm30nt",
]);

export const ISOLATION_TRANSFORMER = "shore-victron-isolation-transformer-3600";

type Component = { node: ComponentNode; def: ComponentDefinition; terms: Terminal[] };

function groundConductor(terminal: Terminal): boolean {
  if (isAcPowerTerminalId(terminal.id)) return false;
  return terminal.polarity === "ground" ||
    (terminal.polarity === "negative" && !isAcTerminalId(terminal.id));
}

/** The PE network is physical copper that remains connected in every state.
 * Both grounding checks use these edges; only their destinations differ. */
export function buildGroundGraph(system: System, graph: Graph, components: Component[]): Graph {
  const admitted = new Map<Vertex, string>();
  const transformers = new Set<string>();
  const switchedNegatives = new Set<string>();
  const contactsByState = new Map<string, Set<string>[]>();
  const contactKey = (link: IntraLink) => JSON.stringify([link.from, link.to].sort());
  for (const { node, def, terms } of components) {
    if (def.id === ISOLATION_TRANSFORMER) transformers.add(node.id);
    const conductors = terms.filter(groundConductor);
    for (const terminal of conductors) admitted.set(v(node.id, terminal.id), node.id);
    // A stock wire splice uses a polarity-neutral drawing post. Two unfused
    // cables at that post continue the same lead. An AC neutral terminal or
    // a branched junction cannot establish a dedicated earth path this way.
    if (def.id === JUNCTION_DEFINITION_ID && matchesAcSafetyIdentity(node, def)) {
      const post = v(node.id, "j");
      const edges = graph.adj.get(post) ?? [];
      if (edges.length === 2 && edges.every(({ via }) => via.kind === "cable" && via.fuseRating == null)) {
        admitted.set(post, node.id);
      }
    }
    if (conductors.length < 2) continue;
    // A negative-side FET BMS can interrupt its return even without modeled
    // states. Only an explicitly permanent connection may bypass the FET.
    if (def.bms?.switching.mode === "fet" && def.bms.switching.leg === "negative") switchedNegatives.add(node.id);
    const options = getSimulateStateSpec(def)?.options;
    if (options) contactsByState.set(node.id, options.map(option => new Set(
      option.ampOverride === 0 ? [] : intraLinks(node, def, system.cables, option).map(contactKey),
    )));
  }

  const continuous = (at: Vertex, to: Vertex, edge: Edge): boolean => {
    if (edge.kind === "cable") return edge.fuseRating == null;
    const link = edge.link;
    if (transformers.has(link.nodeId) || link.kind === "fused-slot" || link.kind === "dc-breaker") return false;
    if (link.kind === "permanent") return true;
    // Chassis symbols share a physical point across nodes, independently of
    // a component's contacts. All ordinary internal links stay on one node.
    if (admitted.get(at) !== admitted.get(to)) return true;
    if (switchedNegatives.has(link.nodeId)) return false;
    const key = contactKey(link);
    return contactsByState.get(link.nodeId)?.every(contacts => contacts.has(key)) ?? true;
  };

  const adj: Graph["adj"] = new Map();
  for (const at of admitted.keys()) {
    adj.set(at, (graph.adj.get(at) ?? []).filter(({ vertex, via }) => admitted.has(vertex) && continuous(at, vertex, via)));
  }
  return { ...graph, adj };
}

function requiresGround(def: ComponentDefinition): boolean {
  // Chargers can declare a separate chassis lug without AC-prefixed ports
  // or an inverter catalog ID. The terminal declaration, not that ID, makes
  // the continuity check applicable (Xantrex Freedom SW installation, DC Grounding).
  return def.category === "source" || def.category === "engine" || def.category === "charger" ||
    (def.category === "load" && (def.watts ?? 0) >= 500) ||
    def.id.includes("inverter") ||
    def.terminals.some(terminal => isAcTerminalId(terminal.id));
}

/**
 * A drawn ground lead must reach a real reference, not merely another lug.
 * ABYC E-11.5.5.3 joins the main AC grounding bus and DC negative; retain that
 * convention, while keeping L/N, signal wiring and converter input/output
 * isolation out of the continuity walk. The lowest negative of a series bank
 * is its reference; a midpoint is not a substitute for the bank negative.
 *
 * This establishes diagram continuity only, not fault-loop impedance or RCD
 * operation. Victron MultiPlus-II installation §4.4 additionally requires
 * uninterruptible casing grounding in mobile installations:
 * https://www.victronenergy.com/media/pg/MultiPlus-II_230V/en/installation.html
 */
export function detectMissingGround(system: System, lookup: DefLookup): Issue[] {
  const terminals = new Map<Vertex, Terminal>();
  const components: Component[] = [];
  const references = new Set<Vertex>();
  const shoreReferences = new Set<Vertex>();
  for (const stack of computeBatteryStacks(system, lookup).stacks) {
    for (const terminal of stack.bottomNegatives) references.add(terminal);
  }
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    const terms = effectiveTerminals(def, node);
    components.push({ node, def, terms });
    for (const terminal of terms) {
      const key = v(node.id, terminal.id);
      terminals.set(key, terminal);
      if (SHORE_INLETS.has(def.id) && matchesAcSafetyIdentity(node, def) && terminal.polarity === "ground") {
        references.add(key);
        shoreReferences.add(key);
      }
    }
  }

  const graph = buildGroundGraph(system, buildGraph(system, lookup), components);
  const connectedToReference = (start: Vertex): boolean => {
    const seen = new Set([start]);
    const queue = [start];
    for (let i = 0; i < queue.length; i++) {
      const at = queue[i];
      // The inlet's own PE is not evidence of any onboard grounding wire.
      if (at !== start && references.has(at)) return true;
      for (const { vertex, via } of graph.adj.get(at) ?? []) {
        const terminal = terminals.get(vertex);
        if (!terminal || seen.has(vertex)) continue;
        // A supplied inlet already represents external earth, but still needs
        // an actual PE lead into the drawing. A neutral or DC return cable
        // cannot satisfy that requirement on its own.
        if (shoreReferences.has(start) && via.kind === "cable" && terminal.polarity === "ground") return true;
        seen.add(vertex);
        queue.push(vertex);
      }
    }
    return false;
  };

  const issues: Issue[] = [];
  for (const { node, def, terms } of components) {
    if (!requiresGround(def)) continue;
    const missing = terms.find(terminal => terminal.polarity === "ground" &&
      !connectedToReference(v(node.id, terminal.id)));
    if (!missing) continue;
    const label = node.label ?? def.shortName ?? def.name;
    issues.push({
      code: "missing-ground", severity: "warning",
      title: { key: "diag.missing-ground.title", vars: { label } },
      message: { key: "diag.missing-ground.message", vars: { label, terminal: missing.label } },
      nodeIds: [node.id],
      fix: { key: "diag.missing-ground.fix" },
    });
  }
  return issues;
}
